import {Injectable, Injector} from "@angular/core";
import {catchError, last, map, merge, mergeMap, Observable, of, share} from "rxjs";
import {JwtHttpClientService} from "../http/jwt-http-client.service";
import {
    MissingTranslationHandler,
    MissingTranslationHandlerParams,
    TranslateDefaultParser,
    TranslateService
} from "@ngx-translate/core";
import {HttpResponse} from "@angular/common/http";

import {DataClientError} from "../error/data-client-error";

@Injectable()
export class I18nService extends TranslateDefaultParser implements MissingTranslationHandler {

    /*
     * Static URL prefix for all server side calls
     */
    public static readonly URL = '/dataserver/i18n/';

    /*
     * Track whether we are loading / have loaded server-side translations for a given language
     */
    public serverTranslationsLoading: { [key: string]: Observable<boolean> } = {};

    private translate: TranslateService;

    /**
     * Constructs the service with a Http service, and Injector service
     * @param http HTTP Service
     * @param injector The angular injector
     */
    constructor(protected http: JwtHttpClientService, private injector: Injector) {
        super();
    }

    /**
     * Handles a missing translation.
     * If the server translations have not been loaded, attempts to reload them for both the current and default language, and then retries the translation.
     * Note that once the default translation has been loaded, ng2-translate will always default to the default translations, so if the current language were to change,
     * the new server-side translations would never be loaded via this handler.  For now, it is assumed that we will not be changing languages on the fly - if this changes,
     * the server-side translations will have to be loaded at the time of switching.
     * @param params
     * @return an observable of the translated value (or the untranslated key if server-side translationshave already been loaded)
     */
    handle(params: MissingTranslationHandlerParams): any {
        return this.serverTranslationsLoaded(params.translateService, params.translateService.currentLang)
            ? this.getDefaultTranslation(params.key)
            : (params.translateService.currentLang === params.translateService.getDefaultLang()
                ? this.loadServerTranslations(params.translateService.currentLang)
                : merge(this.loadServerTranslations(params.translateService.currentLang), this.loadServerTranslations(params.translateService.getDefaultLang()))).pipe(
                last(),
                mergeMap(() => params.translateService.get(params.key, params.interpolateParams)),
                catchError(() => of(this.getDefaultTranslation(params.key)))
            );
    }

    private getDefaultTranslation(key: string): string {
        return key.startsWith("'") && key.endsWith("'") ? key.substring(1, key.length - 1) : "[" + key + "]";
    }

    /**
     * Loads and merges the server-side i18n translations for the specified language.
     * @param language The language code for which to retrieve the translations
     * @return An observable of a single boolean (always true) indicating that the server translations are loaded
     */
    public loadServerTranslations(language: string): Observable<boolean> {
        if (this.serverTranslationsLoaded(this.getTranslateService(), language)) {
            return of(true);
        }
        if (this.serverTranslationsLoading[language]) {
            return this.serverTranslationsLoading[language];
        }
        return this.serverTranslationsLoading[language] = this.http.getWithoutToken(I18nService.URL + language).pipe(
            map((response: HttpResponse<Object>) => {
                this.getTranslateService().setTranslation(language, response.body, true);
                this.serverTranslationsLoading[language] = undefined;
                return true;
            }),
            catchError((error: DataClientError) => {
                console.log("Failed to obtain server-side translations for '" + language + "': " + error.message);
                this.serverTranslationsLoading[language] = undefined;
                throw error;
            }),
            share()
        );
    }

    override interpolate(expr: string, params?: any): string {
        if (typeof expr !== 'string') {
            return expr;
        }
        return expr.replace(this.templateMatcher, (substring, paramKey) => {
            if (params) {
                let paramValue: string = this.getValue(params, paramKey);
                if (typeof paramValue !== 'undefined' && paramValue !== null) {
                    if (typeof paramValue === 'string' && paramValue.indexOf("{{") >= 0) {
                        paramValue = this.interpolate(paramValue, params);
                    }
                    return paramValue;
                }
            }
            try {
                return this.getTranslateService().instant(paramKey, params);
            } catch (err) {
                return paramKey;
            }
        });
    }

    private serverTranslationsLoaded(translateService: TranslateService, language: string): boolean {
        const langTranslations: any = translateService["translations"][language];
        return langTranslations && langTranslations.server;
    }

    /**
     * Obtain the translation service from the injector directly to avoid a provider cyclic dependency
     */
    getTranslateService(): TranslateService {
        if (!this.translate) {
            this.translate = this.injector.get(TranslateService);
        }
        return this.translate;
    }
}
